import React from "react";
import { CHUNK_RELOAD_KEY, hardReloadApp } from "../../helpers/hardReloadApp";
import "./styles.scss";

const RefreshIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 12a8 8 0 1 1-2.34-5.66"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <path
      d="M20 4v5h-5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, refreshing: false, autoReloading: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.log(error, info);

    // Stale tabs still run the previous main bundle. On ChunkLoadError after a
    // deploy, auto-reload once so they pick up the new app (and this UI) instead
    // of sitting on whatever ErrorBoundary was already in memory.
    if (error?.name === "ChunkLoadError" && typeof window !== "undefined") {
      try {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
          this.setState({ autoReloading: true });
          hardReloadApp();
          return;
        }
      } catch {
        // sessionStorage unavailable — show the refresh UI
      }
    }

    this.setState({ hasError: true, error });
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.hasError && !this.state.hasError) {
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch {
        // ignore
      }
    }
  }

  handleRefresh = () => {
    this.setState({ refreshing: true });
    hardReloadApp();
  };

  render() {
    const { hasError, error, refreshing, autoReloading } = this.state;
    const { children } = this.props;

    if (hasError) {
      if (autoReloading) {
        return null;
      }

      const isUpdate = error?.name === "ChunkLoadError";

      return (
        <div className="rv-error-boundary" role="alert">
          <div className="rv-error-boundary__card">
            <div className="rv-error-boundary__icon">
              <RefreshIcon size={26} />
            </div>

            <h1 className="rv-error-boundary__title">
              {isUpdate ? "A new version is ready" : "Something went wrong"}
            </h1>

            <p className="rv-error-boundary__body">
              {isUpdate
                ? "We've shipped some updates. Refresh the page to load the latest version. Your work here wasn't affected."
                : "An unexpected error occurred. Refresh the page and try again."}
            </p>

            <button
              type="button"
              className={`rv-error-boundary__refresh${
                refreshing ? " is-refreshing" : ""
              }`}
              onClick={this.handleRefresh}
              disabled={refreshing}
            >
              <RefreshIcon />
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}
